import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Modal: paste a list of employee emails (one per line, comma- or semicolon-
 * separated) and dispatch the public questionnaire link to each via Resend.
 */
export default function BulkInviteModal({ assessmentId, onClose, onSuccess }) {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parseEmails = (text) => {
    const matches = (text || "").match(/[\w.+-]+@[\w-]+\.[\w.-]+/g) || [];
    return Array.from(new Set(matches.map((m) => m.toLowerCase())));
  };

  const emails = parseEmails(raw);

  const submit = async () => {
    if (emails.length === 0) {
      toast.error("Informe pelo menos 1 e-mail válido.");
      return;
    }
    if (emails.length > 200) {
      toast.error("Limite máximo de 200 e-mails por envio.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/assessments/${assessmentId}/bulk-invite`, { emails });
      setResult(data);
      if (onSuccess) onSuccess();
      if (data.delivered === data.total) {
        toast.success(`${data.delivered} convite(s) enviado(s) com sucesso.`);
      } else {
        toast.warning(`${data.delivered}/${data.total} enviados. ${data.failed} falharam (verifique modo de teste do Resend).`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="bulk-invite-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white w-full max-w-xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-200 flex justify-between items-start bg-slate-50">
          <div>
            <div className="overline text-emerald-900 mb-1">Convite por e-mail</div>
            <h2 className="font-display text-xl font-extrabold tracking-tight">Convidar colaboradores</h2>
            <p className="text-xs text-slate-600 mt-1">
              Cada destinatário receberá o link anônimo do questionário pelo Resend.
            </p>
          </div>
          <button onClick={onClose} className="btn btn-ghost text-sm" data-testid="bulk-invite-close">
            <i className="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!result ? (
            <>
              <div>
                <label className="label">E-mails (um por linha ou separados por vírgula/ponto-e-vírgula)</label>
                <textarea
                  value={raw} onChange={(e) => setRaw(e.target.value)}
                  rows={8}
                  placeholder={"maria@empresa.com\njoao@empresa.com\npedro@empresa.com"}
                  className="input font-mono text-xs"
                  data-testid="bulk-invite-textarea" />
                <div className="text-xs text-slate-500 mt-2 flex justify-between">
                  <span>{emails.length} e-mail(s) válido(s) detectado(s)</span>
                  <span>limite: 200/envio</span>
                </div>
              </div>

              {emails.length > 0 && (
                <div className="card border-slate-200">
                  <div className="overline mb-2">Pré-visualização</div>
                  <div className="text-xs font-mono text-slate-600 max-h-32 overflow-y-auto space-y-0.5">
                    {emails.slice(0, 30).map((e) => <div key={e}>{e}</div>)}
                    {emails.length > 30 && (
                      <div className="text-slate-400 italic">… e mais {emails.length - 30}</div>
                    )}
                  </div>
                </div>
              )}

              <div className="border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                <i className="fa-solid fa-circle-info mr-1"></i>
                Em modo de teste do Resend, os e-mails só são entregues ao endereço verificado da conta.
                Verifique seu domínio em <span className="font-mono">resend.com/domains</span> para enviar a qualquer destinatário.
              </div>
            </>
          ) : (
            <div data-testid="bulk-invite-result">
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="card border-slate-200 text-center">
                  <div className="kpi-number text-2xl text-emerald-900">{result.total}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">total</div>
                </div>
                <div className="card border-slate-200 text-center">
                  <div className="kpi-number text-2xl text-emerald-700">{result.delivered}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">entregues</div>
                </div>
                <div className="card border-slate-200 text-center">
                  <div className="kpi-number text-2xl text-rose-700">{result.failed}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">falharam</div>
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto border border-slate-200">
                {result.results.map((r) => (
                  <div key={r.email} className="flex justify-between items-center py-2 px-3 border-b border-slate-100 text-xs">
                    <span className="font-mono text-slate-700">{r.email}</span>
                    <span className={`font-bold uppercase tracking-wider text-[10px] ${r.delivered ? "text-emerald-700" : "text-rose-700"}`}>
                      {r.delivered ? <><i className="fa-solid fa-circle-check mr-1"></i>entregue</> : <><i className="fa-solid fa-circle-xmark mr-1"></i>falha</>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-slate-200 flex justify-between gap-2 bg-slate-50">
          {!result ? (
            <>
              <button onClick={onClose} className="btn btn-secondary" data-testid="bulk-invite-cancel">Cancelar</button>
              <button onClick={submit} disabled={busy || emails.length === 0}
                      className="btn btn-primary" data-testid="bulk-invite-submit">
                {busy ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-solid fa-paper-plane"></i>}
                Enviar {emails.length > 0 ? `(${emails.length})` : ""}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="btn btn-primary ml-auto" data-testid="bulk-invite-done">
              <i className="fa-solid fa-check"></i> Concluir
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
